import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { CatalogItemBase } from "building-types/entities/catalogItemBase";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type {
  DocEntityHandle,
  PropInstanceHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { FaceCategory } from "building-types/materials/faceCategories";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import type { PropViewNoteTable } from "building-types/settings/viewNotesSettings";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { Table } from "common/data/table";
import type { AABB3 } from "geom/aabb3Types";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { PropCategory } from "./propCategoryTypes";
import { PropId } from "./propIds";
import type { PropBoundOrientation } from "./propTypesEnum";

export const DEFAULT_PROP_ID = PropId.Living_Seat_ArmChairChaise_01;

export interface PropFloorPlanTagProps {
  readonly floorPlanTag?: string;
  readonly floorPlanTagOffsetX?: number;
  readonly floorPlanTagOffsetY?: number;
}

export interface PropSrc {
  readonly src: string; // interior/exterior prop catalog id
  readonly catalogType: PropCatalogType;
}

export interface Prop extends PropSrc, LabelableEntityBase, BuildingEntityBase<"PROP">, PropFloorPlanTagProps {
  readonly room: Room | null;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotationDegrees: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly scaleZ: number;
  readonly mirrorPropX?: boolean;
  readonly mirrorPropY?: boolean;
  readonly handle?: PropInstanceHandle | RoomLayoutInstanceHandle;
  readonly handleAuxiliary?: PropInstanceHandle; // When the handle is RoomLayoutInstanceHandle, this is the handle of the original entity.
  readonly floorMaterial?: InteriorFloorMaterialOverride;
  readonly descriptor?: ViewEntityDescriptor;
  readonly materialOverrides?: PropMaterialOverrides;
  readonly useExteriorPalette?: boolean;
  readonly planOutline?: PolyLine2;
  readonly alignedAABB3?: AABB3;
  readonly alignmentX?: number;
  readonly alignmentY?: number;
  readonly useAlignmentCalc?: boolean;
  readonly entityInstanceType?: EntityInstanceType;
  readonly floorPlanTagProps?: PropFloorPlanTagProps;
  readonly isExterior?: boolean;
}

export interface PropWithLevelId extends Prop {
  readonly levelId: ForeignEntityId<"LEVEL"> | undefined; // undefined for props on dormer windows
  readonly aboveLevel: boolean;
  readonly faceCategory: FaceCategory;

  // Used to select the reporting unit for the prop. Could be the handle of some ancestor entity.
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

export interface PropWithAssetId extends PropWithLevelId {
  assetId: number | ForeignEntityId<"ASSET">; // Prop library index or imported FBX asset ID.
}

export interface SimplePropScaleNotRequired {
  readonly type: "SIMPLE";
  readonly propName: string; // Built-in prop name
  readonly proxyPropName?: string; // The proxy contains simplified geometry for the model, to be used for lightprobe placement.
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly rotX?: number; // in degrees
  readonly rotY?: number; // in degrees
  readonly rotZ?: number; // in degrees
  readonly scale?: number;
  readonly castShadow?: boolean;
}

export interface SimpleProp extends SimplePropScaleNotRequired {
  readonly scale: number;
}

// some fields are required
export interface SimplePropVariant extends SimpleProp {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotX: number; // in degrees
  readonly rotY: number; // in degrees
  readonly rotZ: number; // in degrees
  readonly scale: number;
  readonly castShadow: boolean;
}

/**
 * Table keys should be type {@link PaletteMaterialSlot} or a cache key
 * of a {@link MaterialSrc} prefixed with MATSRC_ (e.g. MATSRC_17__289).
 */

export interface FullMaterialSlot {
  readonly paletteMaterialSlot: PaletteMaterialSlot;
  readonly useExteriorPalette: boolean;
}

export type PropMaterialOverrides = Table<FullMaterialSlot>;

/**
 * This is the runtime type for prop attributes, which can be a builtin prop or a custom prop.
 */
export interface PropAttributes extends LabelableEntityBase, DocEntityBase<"PROP">, CatalogItemBase<"PROP"> {
  readonly id: PrimaryEntityId<"PROP">;
  readonly name: string;
  readonly propAssetId: number | ForeignEntityId<"ASSET">;
  readonly category: PropCategory;
  readonly floorPlanTag?: string;
  readonly defaultVerticalOffset?: number;
  readonly floorMaterialOverride?: InteriorFloorMaterialOverride;
  readonly floorPlanTagOffsetX?: number;
  readonly floorPlanTagOffsetY?: number;
  readonly useExteriorPalette?: boolean;
  readonly isPlumbingArchitecturalProp?: boolean;
  readonly excludeFromFoundationPlanDimensions?: boolean;
  readonly isUtilityArchitecturalProp?: boolean;
  readonly isElectricalArchitecturalProp?: boolean;
  readonly hideTagOnElectricalPlan?: boolean;
  readonly takeoffDescription?: string;
  readonly propBoundOrientation?: PropBoundOrientation;
  readonly viewNotes?: PropViewNoteTable;
  readonly materialOverrides?: PropMaterialOverrides;
  readonly uniformScale?: boolean;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly scaleZ?: number;
}

/**
 * This is the type that is stored in the catalog in the Doc and can be rekeyed. It does not include
 * builtin props.
 */
export interface PropAttributesCustom extends PropAttributes {
  readonly id: PrimaryEntityId<"PROP">;
}

export type PropAttributeTable = Table<PropAttributes>;

/**
 * Prop material parameters with pre-resolved catalog attributes.
 * Use this interface when you don't have access to a PropAttributeTable,
 * or when you want to pre-compute the merged material overrides.
 */
export interface PropMaterialParams {
  /** Pre-merged material overrides (catalog + prop-specific) */
  mergedMaterialOverrides: PropMaterialOverrides;
  /** Whether to use exterior palette (resolved from isExterior, prop, or catalog) */
  useExteriorPalette: boolean;
  /** Room type for palette material lookup */
  roomType?: RoomType;
}
